import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./training.css";

/* Training Evaluation — ported from the prototype (evaluate-training.html).
   Navy numbered section headers (View Claim theme), 1–5 rating rows, learning
   outcome, completion evidence, and the sidebar guide cards. */

const RATING_ROWS = [
  { name: "relevance", label: "Training content was relevant to my role." },
  { name: "trainer", label: "The trainer delivered the topic effectively." },
  { name: "materials", label: "The training materials were useful and clear." },
  { name: "overall", label: "Overall training experience." },
];

const TrainingEvaluatePage = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);
  };

  const handleSaveDraft = () => {
    setDraftSaved(true);
    window.setTimeout(() => setDraftSaved(false), 2500);
  };

  return (
    <div className="bes-training bes-training-evaluate">
      <div className="training-page-head">
        <div className="title-left">
          <div className="page-icon evaluate-icon">
            <i className="ri-survey-line" />
          </div>
          <div>
            <h1>Training Evaluation</h1>
            <p>Submit feedback and outcome assessment for the completed training.</p>
          </div>
        </div>
        <Link to="/training/request" className="btn btn-outline">
          <i className="ri-arrow-left-line" /> Back to Training
        </Link>
      </div>

      <div className="evaluation-layout">
        <form onSubmit={handleSubmit}>
          {/* 1. Training Details */}
          <div className="panel eval-card">
            <div className="panel-header">
              <div className="detail-section-title">
                <span>1</span>
                <h2 className="panel-title">Training Details</h2>
              </div>
              <span className="status-badge status-completed">Completed</span>
            </div>
            <div className="panel-body">
              <div className="readonly-card">
                <div className="readonly-grid">
                  <div className="readonly-item">
                    <label>Request ID</label>
                    <div>#TR-2026-015</div>
                  </div>
                  <div className="readonly-item">
                    <label>Status</label>
                    <div>Completed</div>
                  </div>
                  <div className="readonly-item">
                    <label>Course Name</label>
                    <div>Certified Quality Assurance Professional</div>
                  </div>
                  <div className="readonly-item">
                    <label>Training Provider</label>
                    <div>QA Academy Malaysia</div>
                  </div>
                  <div className="readonly-item">
                    <label>Training Dates</label>
                    <div>10 February 2026 – 14 February 2026</div>
                  </div>
                  <div className="readonly-item">
                    <label>Training Category</label>
                    <div>Compliance &amp; Regulatory</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Evaluation Rating */}
          <div className="panel eval-card">
            <div className="panel-header">
              <div className="detail-section-title">
                <span>2</span>
                <h2 className="panel-title">Evaluation Rating</h2>
              </div>
            </div>
            <div className="panel-body">
              <div className="rating-grid">
                {RATING_ROWS.map((row) => (
                  <div className="rating-row" key={row.name}>
                    <div className="rating-label">{row.label}</div>
                    <div className="rating-options">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <label key={value}>
                          <input type="radio" name={row.name} value={value} required={value === 1} />
                          {value}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 3. Learning Outcome */}
          <div className="panel eval-card">
            <div className="panel-header">
              <div className="detail-section-title">
                <span>3</span>
                <h2 className="panel-title">Learning Outcome</h2>
              </div>
            </div>
            <div className="panel-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>
                    Key learning gained <span className="required">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="learning"
                    placeholder="Describe key learning points gained from this training..."
                    required
                  />
                </div>
                <div className="form-group full">
                  <label>
                    How will this be applied at work? <span className="required">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="application"
                    placeholder="Explain how the knowledge or skills will be applied in your daily work..."
                    required
                  />
                </div>
                <div className="form-group full">
                  <label>Suggestion / Additional comments</label>
                  <textarea
                    className="form-control"
                    name="comments"
                    placeholder="Optional comments or suggestions for future training..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 4. Completion Evidence */}
          <div className="panel eval-card">
            <div className="panel-header">
              <div className="detail-section-title">
                <span>4</span>
                <h2 className="panel-title">Completion Evidence</h2>
              </div>
            </div>
            <div className="panel-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Certificate / Attendance Attachment</label>
                  <input className="form-control" type="file" accept=".pdf,.jpg,.jpeg,.png" />
                </div>
                <div className="form-group">
                  <label>
                    Completion Date <span className="required">*</span>
                  </label>
                  <input className="form-control" type="date" defaultValue="2026-02-14" required />
                </div>
              </div>
            </div>
          </div>

          <div className="footer-actions">
            <Link to="/training/request" className="btn btn-outline">
              Cancel
            </Link>
            <div className="footer-actions-right">
              {draftSaved && <span className="draft-saved-note">Draft saved.</span>}
              <button type="button" className="btn btn-outline" onClick={handleSaveDraft}>
                Save as Draft
              </button>
              <button type="submit" className="btn btn-primary">
                Submit Evaluation
              </button>
            </div>
          </div>
        </form>

        <aside>
          <div className="summary-card eval-summary-card">
            <h3 className="eval-summary-header">Evaluation Guide</h3>
            {[
              ["1", "Very Poor"],
              ["2", "Poor"],
              ["3", "Average"],
              ["4", "Good"],
              ["5", "Excellent"],
            ].map(([score, label]) => (
              <div className="guide-line" key={score}>
                <span className="guide-score">{score}</span>
                <span className="guide-label">{label}</span>
              </div>
            ))}
          </div>

          <div className="summary-card eval-summary-card">
            <h3 className="eval-summary-header">Submission Requirement</h3>
            <div className="summary-line">
              <span>Evaluation</span>
              <span>Required</span>
            </div>
            <div className="summary-line">
              <span>Review By</span>
              <span>Immediate Superior</span>
            </div>
            <div className="summary-line">
              <span>Due Date</span>
              <span>21 Feb 2026</span>
            </div>
          </div>

          <div className="notice">
            <strong>Note:</strong> Once submitted, the evaluation will be routed to your immediate superior for review.
          </div>
        </aside>
      </div>

      {submitted && (
        <div className="success-modal">
          <div className="success-box">
            <div className="success-icon">
              <i className="ri-check-line" />
            </div>
            <h3>Evaluation Submitted</h3>
            <p>Your training evaluation has been submitted successfully.</p>
            <button type="button" className="btn btn-primary" onClick={() => navigate("/training/request")}>
              Back to Training
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingEvaluatePage;
